import React, { useState, type FormEvent } from "react";
import {
  astryx,
  AuthCard,
  AuthLayout,
  AuthLink,
  AuthSubmitButton,
  AuthTextInput,
} from "@shadowcue/ui";
import useNativeFormSubmit from "../../hooks/useNativeFormSubmit";

interface ForgotPasswordProps {
  formAction: string;
  authenticityToken: string;
  /** The submitted email, kept when Devise re-renders with an error. */
  email: string | null;
  /** Devise's validation message for the email, e.g. "Email not found". */
  emailError: string | null;
  signInPath: string;
}

/**
 * Requests a password reset email. Posts natively to Devise, which redirects
 * back to sign-in with a notice on success, or re-renders this page with the
 * error on the email field.
 */
const ForgotPassword = (props: ForgotPasswordProps) => {
  const [email, setEmail] = useState<string>(props.email ?? "");
  const { isSubmitting, markSubmitting } = useNativeFormSubmit();

  // Once the email is edited, Devise's error was about a different address.
  const isShowingEmailError = props.emailError !== null && email === (props.email ?? "");
  const canSubmit = email.trim() !== "" && !isSubmitting;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (!canSubmit) {
      event.preventDefault();
      return;
    }
    markSubmitting();
  };

  return (
    <AuthLayout>
      <form method="post" action={props.formAction} onSubmit={onSubmit}>
        <input type="hidden" name="authenticity_token" value={props.authenticityToken} />
        <AuthCard subtitle="Enter your email and we'll send you a reset link.">
          <AuthTextInput
            type="email"
            label="Email"
            value={email}
            onChange={setEmail}
            isLabelHidden
            placeholder="Email"
            htmlName="user[email]"
            status={isShowingEmailError ? { type: "error", message: props.emailError ?? "" } : undefined}
            // Matches the password fields, whose InputGroup always detaches.
            statusVariant="detached"
            hasAutoFocus
            isRequired
            width="100%"
          />

          <astryx.Stack direction="vertical" gap={3}>
            <AuthSubmitButton
              type="submit"
              label={isSubmitting ? "Sending…" : "Send reset link"}
              variant="primary"
              width="100%"
              isDisabled={!canSubmit}
              isLoading={isSubmitting}
            />
            <astryx.Stack direction="horizontal" justify="center">
              <AuthLink href={props.signInPath}>
                Back to sign in
              </AuthLink>
            </astryx.Stack>
          </astryx.Stack>
        </AuthCard>
      </form>
    </AuthLayout>
  );
};

export default ForgotPassword;
